import {
	GetActiveMarketsParamsDto,
	ActiveMarketsDto,
	GetMarketsParamsDto,
	CreateMarketDto,
	UpdateMarketDto,
} from '@sportspredict/shared';
import api from './base-api';
import type { GetMarketsListDto, ListWithTotals } from '@sportspredict/shared';

export const marketsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// PUBLIC
		getMarketCategories: builder.query({
			query: (categoryId: any) => ({
				url: `markets/categories?category_id=${categoryId}`,
				method: 'GET',
			}),
			extraOptions: { skipAuth: true },
		}),
		//ToDo - change any to market dt once merged with file routing
		getMarkets: builder.query<any[], GetMarketsParamsDto>({
			query: ({ event_id, lobby_id, sort } = {}) => {
				const params = new URLSearchParams();
				if (event_id) params.set('event_id', event_id);
				if (lobby_id) params.set('lobby_id', lobby_id);
				if (sort) params.set('sort', sort);
				return { url: `markets?${params.toString()}`, method: 'GET' };
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }: any) => ({ type: 'Market' as const, id })),
							{ type: 'Market' as const, id: 'LIST' },
						]
					: [{ type: 'Market' as const, id: 'LIST' }],
			extraOptions: { skipAuth: true },
		}),
		// PRIVATE
		getUserSecondChanceRank: builder.query({
			query: ({ eventId, lobbyId }: any) => ({
				url: `lobbies/second-chance/rank/${eventId}/${lobbyId}`,
				method: 'GET',
			}),
		}),
		getActiveMarkets: builder.query<ActiveMarketsDto, GetActiveMarketsParamsDto>({
			query: ({ skip, limit, search, category_id } = {}) => {
				const params = new URLSearchParams();
				if (skip !== undefined) params.set('skip', String(skip));
				if (limit !== undefined) params.set('limit', String(limit));
				if (search) params.set('search', search);
				if (category_id) params.set('category_id', category_id);
				return { url: `markets/active?${params.toString()}`, method: 'GET' };
			},
			providesTags: [{ type: 'Market', id: 'ACTIVE_LIST' }],
		}),
		getMarketsList: builder.query<ListWithTotals<any>, GetMarketsListDto>({
			query: (body) => ({ url: 'markets/list', method: 'POST', body }),
			providesTags: (result, error, { eventId }) =>
				result
					? [
							...result.list.map((m: any) => ({ type: 'Market' as const, id: m.id })),
							{ type: 'Market' as const, id: `LIST-${eventId}` },
						]
					: [{ type: 'Market' as const, id: `LIST-${eventId}` }],
		}),
		createMarket: builder.mutation<any, CreateMarketDto>({
			query: (body) => ({ url: 'markets', method: 'POST', body }),
			invalidatesTags: [
				{ type: 'Market', id: 'LIST' },
				{ type: 'Market', id: 'ACTIVE_LIST' },
			],
		}),
		updateMarket: builder.mutation<any, UpdateMarketDto>({
			query: (body) => ({ url: 'markets', method: 'PUT', body }),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: 'Market' as const, id },
				{ type: 'Market' as const, id: 'LIST' },
				{ type: 'Market' as const, id: 'ACTIVE_LIST' },
			],
		}),
		deleteMarket: builder.mutation<void, string>({
			query: (id) => ({ url: `markets/${id}`, method: 'DELETE' }),
			invalidatesTags: (_result, _error, id) => [
				{ type: 'Market' as const, id },
				{ type: 'Market' as const, id: 'LIST' },
				{ type: 'Market' as const, id: 'ACTIVE_LIST' },
			],
		}),
	}),
});

export default marketsApi;

export const {
	useGetMarketCategoriesQuery,
	useGetMarketsQuery,
	useGetUserSecondChanceRankQuery,
	useGetActiveMarketsQuery,
	useGetMarketsListQuery,
	useLazyGetMarketsListQuery,
	useCreateMarketMutation,
	useUpdateMarketMutation,
	useDeleteMarketMutation,
} = marketsApi;
